import { supabase } from '../config/supabase';

interface CalculatorEvent {
  event: string;
  calculator_type?: string;
  input_field?: string;
  query?: string;
  suggestion?: string;
  error_message?: string;
  timestamp: string;
}

class AnalyticsService {
  private async trackEvent(event: CalculatorEvent) {
    // Log to console
    console.log('📊 Analytics:', event.event, event);
    
    // Dispatch custom event for UI updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('analytics-event', { detail: event }));
    }

    // Persist to Supabase if possible
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { event: eventName, calculator_type, ...metadata } = event;
      
      /*
      await supabase.from('analytics_events').insert({
        user_id: user?.id,
        event_name: eventName,
        calculator_id: calculator_type,
        metadata: metadata
      });
      */
    } catch (err) {
      // Fail silently for analytics
    }
  }

  // Calculator Events
  calculatorOpened(calculatorType: string) {
    this.trackEvent({
      event: 'calculator-opened',
      calculator_type: calculatorType,
      timestamp: new Date().toISOString()
    });
  }

  calculationStarted(calculatorType: string) {
    this.trackEvent({
      event: 'calculation-started',
      calculator_type: calculatorType,
      timestamp: new Date().toISOString()
    });
  }

  calculationPdf(calculatorType: string) {
    this.trackEvent({
      event: 'calculation-pdf',
      calculator_type: calculatorType,
      timestamp: new Date().toISOString()
    });
  }

  calculationCompleted(calculatorType: string) {
    this.trackEvent({
      event: 'calculation-completed',
      calculator_type: calculatorType,
      timestamp: new Date().toISOString()
    });
  }

  calculationSaved(calculatorType: string) {
    this.trackEvent({
      event: 'calculation-saved',
      calculator_type: calculatorType,
      timestamp: new Date().toISOString()
    });
  }

  calculationShared(calculatorType: string) {
    this.trackEvent({
      event: 'calculation-shared',
      calculator_type: calculatorType,
      timestamp: new Date().toISOString()
    });
  }

  inputFieldChanged(calculatorType: string, fieldName: string) {
    this.trackEvent({
      event: 'input-field-changed',
      calculator_type: calculatorType,
      input_field: fieldName,
      timestamp: new Date().toISOString()
    });
  }

  calculatorReset(calculatorType: string) {
    this.trackEvent({
      event: 'calculator-reset',
      calculator_type: calculatorType,
      timestamp: new Date().toISOString()
    });
  }

  // AI Chat/Search Events
  aiQuerySubmitted(query: string) {
    this.trackEvent({
      event: 'ai-query-submitted',
      query,
      timestamp: new Date().toISOString()
    });
  }

  aiResponseReceived(query: string) {
    this.trackEvent({
      event: 'ai-response-received',
      query,
      timestamp: new Date().toISOString()
    });
  }

  aiQueryFailed(query: string, errorMessage: string) {
    this.trackEvent({
      event: 'ai-query-failed',
      query,
      error_message: errorMessage,
      timestamp: new Date().toISOString()
    });
  }

  aiSuggestionClicked(suggestion: string) {
    this.trackEvent({
      event: 'ai-suggestion-clicked',
      suggestion,
      timestamp: new Date().toISOString()
    });
  }

  aiResultShared(query: string) {
    this.trackEvent({
      event: 'ai-result-shared',
      query,
      timestamp: new Date().toISOString()
    });
  }

  // Credit System Events
  creditConsumed(remainingCredits: number) {
    this.trackEvent({
      event: 'credit-consumed',
      query: remainingCredits.toString(),
      timestamp: new Date().toISOString()
    });
  }

  creditInsufficient() {
    this.trackEvent({
      event: 'credit-insufficient',
      timestamp: new Date().toISOString()
    });
  }

  loginPromptShown(remainingCredits: number) {
    this.trackEvent({
      event: 'login-prompt-shown',
      query: remainingCredits.toString(),
      timestamp: new Date().toISOString()
    });
  }

  userAuthenticatedForCredits() {
    this.trackEvent({
      event: 'user-authenticated-for-credits',
      timestamp: new Date().toISOString()
    });
  }

  creditRenewed(newTotal: number) {
    this.trackEvent({
      event: 'credit-renewed',
      query: newTotal.toString(),
      timestamp: new Date().toISOString()
    });
  }

  subscriptionModalOpened() {
    this.trackEvent({
      event: 'subscription-modal-opened',
      timestamp: new Date().toISOString()
    });
  }

  freeCreditsExhausted() {
    this.trackEvent({
      event: 'free-credits-exhausted',
      timestamp: new Date().toISOString()
    });
  }

  // Payment Events
  subscriptionPlanViewed(planName: string) {
    this.trackEvent({
      event: 'subscription-plan-viewed',
      query: planName,
      timestamp: new Date().toISOString()
    });
  }

  paymentInitiated(planName: string) {
    this.trackEvent({
      event: 'payment-initiated',
      query: planName,
      timestamp: new Date().toISOString()
    });
  }

  paymentCompleted(planName: string) {
    this.trackEvent({
      event: 'payment-completed',
      query: planName,
      timestamp: new Date().toISOString()
    });
  }

  paymentFailed(planName: string, errorMessage: string) {
    this.trackEvent({
      event: 'payment-failed',
      query: planName,
      error_message: errorMessage,
      timestamp: new Date().toISOString()
    });
  }

  subscriptionUpgraded(planName: string) {
    this.trackEvent({
      event: 'subscription-upgraded',
      query: planName,
      timestamp: new Date().toISOString()
    });
  }

  subscriptionCancelled(planName: string) {
    this.trackEvent({
      event: 'subscription-cancelled',
      query: planName,
      timestamp: new Date().toISOString()
    });
  }

  // Navigation Events
  pageViewed(pageName: string) {
    this.trackEvent({
      event: 'page-viewed',
      query: pageName,
      timestamp: new Date().toISOString()
    });
  }

  blogPageViewed(slug?: string) {
    this.trackEvent({
      event: 'blog-page-viewed',
      query: slug || 'index',
      timestamp: new Date().toISOString()
    });
  }

  pricingPageViewed() {
    this.trackEvent({
      event: 'pricing-page-viewed',
      timestamp: new Date().toISOString()
    });
  }

  comparePageViewed(topic?: string) {
    this.trackEvent({
      event: 'compare-page-viewed',
      query: topic || 'index',
      timestamp: new Date().toISOString()
    });
  }

  bestPageViewed(topic?: string) {
    this.trackEvent({
      event: 'best-page-viewed',
      query: topic || 'index',
      timestamp: new Date().toISOString()
    });
  }

  legalPageViewed(pageType: string) {
    this.trackEvent({
      event: 'legal-page-viewed',
      query: pageType,
      timestamp: new Date().toISOString()
    });
  }

  contactPageViewed() {
    this.trackEvent({
      event: 'contact-page-viewed',
      timestamp: new Date().toISOString()
    });
  }

  aboutPageViewed() {
    this.trackEvent({
      event: 'about-page-viewed',
      timestamp: new Date().toISOString()
    });
  }

  navigationClicked(destination: string) {
    this.trackEvent({
      event: 'navigation-clicked',
      query: destination,
      timestamp: new Date().toISOString()
    });
  }

  menuOpened() {
    this.trackEvent({
      event: 'menu-opened',
      timestamp: new Date().toISOString()
    });
  }

  searchPerformed(searchTerm: string) {
    this.trackEvent({
      event: 'search-performed',
      query: searchTerm,
      timestamp: new Date().toISOString()
    });
  }

  filterApplied(filterType: string) {
    this.trackEvent({
      event: 'filter-applied',
      query: filterType,
      timestamp: new Date().toISOString()
    });
  }

  // Content Interaction Events
  dashboardLoaded() {
    this.trackEvent({
      event: 'dashboard-loaded',
      timestamp: new Date().toISOString()
    });
  }

  recentCalculationClicked(calculationType: string) {
    this.trackEvent({
      event: 'recent-calculation-clicked',
      calculator_type: calculationType,
      timestamp: new Date().toISOString()
    });
  }

  historyItemViewed(itemType: string) {
    this.trackEvent({
      event: 'history-item-viewed',
      query: itemType,
      timestamp: new Date().toISOString()
    });
  }

  pdfExported(documentType: string) {
    this.trackEvent({
      event: 'pdf-exported',
      query: documentType,
      timestamp: new Date().toISOString()
    });
  }

  calculationDeleted(calculationType: string) {
    this.trackEvent({
      event: 'calculation-deleted',
      calculator_type: calculationType,
      timestamp: new Date().toISOString()
    });
  }

  // Performance Events
  appLaunched() {
    this.trackEvent({
      event: 'app-launched',
      timestamp: new Date().toISOString()
    });
  }

  apiRequestStarted(endpoint: string) {
    this.trackEvent({
      event: 'api-request-started',
      query: endpoint,
      timestamp: new Date().toISOString()
    });
  }

  apiRequestCompleted(endpoint: string, duration: number) {
    this.trackEvent({
      event: 'api-request-completed',
      query: `${endpoint}:${duration}ms`,
      timestamp: new Date().toISOString()
    });
  }

  apiRequestFailed(endpoint: string, errorMessage: string) {
    this.trackEvent({
      event: 'api-request-failed',
      query: endpoint,
      error_message: errorMessage,
      timestamp: new Date().toISOString()
    });
  }

  pageLoadTime(pageName: string, loadTime: number) {
    this.trackEvent({
      event: 'page-load-time',
      query: `${pageName}:${loadTime}ms`,
      timestamp: new Date().toISOString()
    });
  }

  errorOccurred(errorType: string, errorMessage: string) {
    this.trackEvent({
      event: 'error-occurred',
      query: errorType,
      error_message: errorMessage,
      timestamp: new Date().toISOString()
    });
  }

  // Error Tracking Events
  javascriptError(errorMessage: string) {
    this.trackEvent({
      event: 'javascript-error',
      error_message: errorMessage,
      timestamp: new Date().toISOString()
    });
  }

  networkError(errorMessage: string) {
    this.trackEvent({
      event: 'network-error',
      error_message: errorMessage,
      timestamp: new Date().toISOString()
    });
  }

  validationError(fieldName: string, errorMessage: string) {
    this.trackEvent({
      event: 'validation-error',
      input_field: fieldName,
      error_message: errorMessage,
      timestamp: new Date().toISOString()
    });
  }

  serviceUnavailable(serviceName: string) {
    this.trackEvent({
      event: 'service-unavailable',
      query: serviceName,
      timestamp: new Date().toISOString()
    });
  }

  // Mobile-Specific Events
  appBackgrounded() {
    this.trackEvent({
      event: 'app-backgrounded',
      timestamp: new Date().toISOString()
    });
  }

  appForegrounded() {
    this.trackEvent({
      event: 'app-foregrounded',
      timestamp: new Date().toISOString()
    });
  }

  pushNotificationReceived(notificationType: string) {
    this.trackEvent({
      event: 'push-notification-received',
      query: notificationType,
      timestamp: new Date().toISOString()
    });
  }

  deepLinkOpened(linkType: string) {
    this.trackEvent({
      event: 'deep-link-opened',
      query: linkType,
      timestamp: new Date().toISOString()
    });
  }
}

export const analyticsService = new AnalyticsService();